import { AfterContentInit, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Route, Router } from '@angular/router';
import { BaseService } from '../../base.service';
import { Subscription } from 'rxjs';
import { scaleIn400ms } from '../../../../../@vex/animations/scale-in.animation';
import { fadeInRight400ms } from '../../../../../@vex/animations/fade-in-right.animation';
import { stagger40ms } from '../../../../../@vex/animations/stagger.animation';
import { fadeInUp400ms } from '../../../../../@vex/animations/fade-in-up.animation';
import { scaleFadeIn400ms } from '../../../../../@vex/animations/scale-fade-in.animation';
import { ENUM_PERMISSIONS } from '../../../enums/permission.enum';
import { Link } from '../../../../../@vex/interfaces/link.interface';
import { TabsManagementInterface } from '../../interfaces/tabs-management.interface';
import { AuthService } from '../../../services/auth.service';
import { PermissionGuard } from '../../../guards/permission.guard';

// @ts-ignore
// @ts-ignore
// @ts-ignore
@Component({
  selector: 'details-base',
  template: '',
  animations: [scaleIn400ms, fadeInRight400ms, stagger40ms, fadeInUp400ms, scaleFadeIn400ms],
})
export class DetailsBaseComponent<T> implements OnInit, OnDestroy, AfterContentInit, TabsManagementInterface {
  ENUM_PERMISSION = ENUM_PERMISSIONS;
  id: string;
  object: T;
  loading = false;
  defaultTabs: Link[] = [];
  protected subscription = new Subscription();
  public tabs: Link[] = [];

  // inject() pour ne pas modifier la signature du constructeur héritée par tous les écrans de détail.
  private readonly detailsBaseRouter = inject(Router);

  // DON'T FORGET TO INJECT SERVICE AND ACTIVEDROUTE IN YOUR PARENT CONTRUSCTOR
  constructor(
    public service: BaseService<T>,
    public activatedRoute: ActivatedRoute,
    public authService: AuthService,
  ) {}

  ngOnInit() {
    this.tabs = this.defaultTabs;
    this.initData();
  }

  ngAfterContentInit() {
    // this.subscription.unsubscribe();
  }

  initData() {
    // Écoute les changements de paramètres de route (ex: navigation entre /engagement/1 et /engagement/2)
    const paramsSub = this.activatedRoute.params.subscribe(params => {
      if (params?.id) {
        this.loading = true;
        this.id = params.id;

        // CRUCIAL : Réinitialiser l'entité AVANT de charger le nouveau ticket
        // Cela empêche les composants enfants de réagir à l'ancien état (race condition)
        this.service.resetEntity();

        const item = this.service.findById(this.id).subscribe({
          next: object => {
            this.object = object.data;
            this.loading = false;
          },
          error: error => {
            this.loading = false;
            // Accès refusé par le back (contrôle que le guard de route ne peut pas faire, ex. jeu
            // régularisation résolu sur l'entité) : page forbidden plutôt qu'un écran figé.
            if (error?.status === 403) {
              this.detailsBaseRouter
                .navigate(['/forbidden'], {
                  state: { permission: this.activatedRoute.snapshot.data?.permission },
                })
                .then();
            }
          },
        });
        this.subscription.add(item);
      }
    });
    this.subscription.add(paramsSub);
  }

  ngOnDestroy() {
    this.service.resetEntity();
    this.subscription.unsubscribe();
  }

  previousRoute() {
    window.history.back();
  }

  addTabs(...tabs: Link[]): void {
    this.tabs = [...this.tabs, ...tabs];
  }

  removeTabs(...tabs: Link[]): void {
    this.tabs = this.tabs.filter(x => !tabs.includes(x));
  }

  getDisplayableTabs(allowDuplicates: boolean = false): Link[] {
    let res: Link[] = [];
    // Filter tabs by permissions
    res = this.tabs?.filter(tab => {
      if (typeof tab.route === 'string') {
        // Get the route related to the tab from the router
        const route = this.findRoute(tab.route?.replace('./', '')); // Implement this function to find ActivatedRoute by path

        // If the route is found and has a permission, it takes priority over the tab permission
        if (route) {
          // Only continue if the route has the PermissionGuard, otherwise we consider it accessible
          if (route.canActivate?.some(x => x.name === PermissionGuard.name)) {
            if (route && route.data && 'permission' in route.data) {
              // Check if the associated route has a permission
              // `hasPermission` est variadique : un tableau doit être étalé, sinon la
              // comparaison porte sur le tableau lui-même et échoue toujours.
              return this.authService.hasPermission(...this.toPermissionArray(route.data.permission));
            } else {
              return false; // If the route doesn't have a permission but has the PermissionGuard, it's not accessible
            }
          }
        }

        // If the route is not found or doesn't have a permission, check the tab permission
        if ('permission' in tab) {
          // Check if the tab has a permission (tableau = OU logique)
          return this.authService.hasPermission(...this.toPermissionArray(tab.permission));
        }

        if ('predikTab' in tab) {
          return this.authService.isPredik();
        }

        if ('hidden' in tab) {
          return tab.hidden;
        }
      }

      return true; // Keep the tab if it doesn't have a permission or associated route permission
    });

    // if (res?.some(x => x.additionalCondition)) {
    res = res?.filter(x => !('additionalCondition' in x) || x.additionalCondition);
    // }

    if (!allowDuplicates) {
      res = res?.filter((x, i) => res.findIndex(y => y.label === x.label) === i);
    }

    const sorted = res?.filter(x => x.index).sort((a, b) => a.index - b.index);

    if (sorted?.length) {
      const unsorted = res?.filter(x => !x.index);
      res = [...sorted, ...unsorted];
    }

    return res;
  }

  /** Normalise un code ou un tableau de codes en tableau, pour l'appel variadique de `hasPermission`. */
  private toPermissionArray(permission: ENUM_PERMISSIONS | ENUM_PERMISSIONS[]): ENUM_PERMISSIONS[] {
    if (permission === null || permission === undefined) {
      return [];
    }
    return Array.isArray(permission) ? permission : [permission];
  }

  // Function to find ActivatedRoute by path
  private findRoute(path: string): Route | undefined {
    const findRoute = (route: Route): Route | undefined => {
      if (route.path === path) {
        return route;
      }

      if (Array.isArray(route.children)) {
        for (const child of route.children) {
          const found = findRoute(child);
          if (found) {
            return found;
          }
        }
      }

      return undefined;
    };

    return findRoute(this.activatedRoute?.snapshot?.routeConfig);
  }
}
